/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */
import { __awaiter } from "tslib";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers";
import * as Parameters from "../models/parameters";
import { LroEngine } from "@azure/core-lro";
import { LroImpl } from "../lroImpl";
/** Class containing SubscriptionOperations operations. */
export class SubscriptionOperationsImpl {
    /**
     * Initialize a new instance of the class SubscriptionOperations class.
     * @param client Reference to the service client
     */
    constructor(client) {
        this.client = client;
    }
    /**
     * The operation to cancel a subscription
     * @param subscriptionId Subscription Id.
     * @param options The options parameters.
     */
    cancel(subscriptionId, options) {
        return this.client.sendOperationRequest({ subscriptionId, options }, cancelOperationSpec);
    }
    /**
     * The operation to rename a subscription
     * @param subscriptionId Subscription Id.
     * @param body Subscription Name
     * @param options The options parameters.
     */
    rename(subscriptionId, body, options) {
        return this.client.sendOperationRequest({ subscriptionId, body, options }, renameOperationSpec);
    }
    /**
     * The operation to enable a subscription
     * @param subscriptionId Subscription Id.
     * @param options The options parameters.
     */
    enable(subscriptionId, options) {
        return this.client.sendOperationRequest({ subscriptionId, options }, enableOperationSpec);
    }
    /**
     * Accept subscription ownership.
     * @param subscriptionId Subscription Id.
     * @param body The parameters required to accept subscription ownership.
     * @param options The options parameters.
     */
    beginAcceptOwnership(subscriptionId, body, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const directSendOperation = (args, spec) => __awaiter(this, void 0, void 0, function* () {
                return this.client.sendOperationRequest(args, spec);
            });
            const sendOperation = (args, spec) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                let currentRawResponse = undefined;
                const providedCallback = (_a = args.options) === null || _a === void 0 ? void 0 : _a.onResponse;
                const callback = (rawResponse, flatResponse) => {
                    currentRawResponse = rawResponse;
                    providedCallback === null || providedCallback === void 0 ? void 0 : providedCallback(rawResponse, flatResponse);
                };
                const updatedArgs = Object.assign(Object.assign({}, args), { options: Object.assign(Object.assign({}, args.options), { onResponse: callback }) });
                const flatResponse = yield directSendOperation(updatedArgs, spec);
                return {
                    flatResponse,
                    rawResponse: {
                        statusCode: currentRawResponse.status,
                        body: currentRawResponse.parsedBody,
                        headers: currentRawResponse.headers.toJSON()
                    }
                };
            });
            const lro = new LroImpl(sendOperation, { subscriptionId, body, options }, acceptOwnershipOperationSpec);
            return new LroEngine(lro, {
                resumeFrom: options === null || options === void 0 ? void 0 : options.resumeFrom,
                intervalInMs: options === null || options === void 0 ? void 0 : options.updateIntervalInMs
            });
        });
    }
    /**
     * Accept subscription ownership.
     * @param subscriptionId Subscription Id.
     * @param body The parameters required to accept subscription ownership.
     * @param options The options parameters.
     */
    beginAcceptOwnershipAndWait(subscriptionId, body, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const poller = yield this.beginAcceptOwnership(subscriptionId, body, options);
            return poller.pollUntilDone();
        });
    }
    /**
     * Accept subscription ownership status.
     * @param subscriptionId Subscription Id.
     * @param options The options parameters.
     */
    acceptOwnershipStatus(subscriptionId, options) {
        return this.client.sendOperationRequest({ subscriptionId, options }, acceptOwnershipStatusOperationSpec);
    }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);
const cancelOperationSpec = {
    path: "/subscriptions/{subscriptionId}/providers/Microsoft.Subscription/cancel",
    httpMethod: "POST",
    responses: {
        200: {
            bodyMapper: Mappers.CanceledSubscriptionId
        },
        default: {
            bodyMapper: Mappers.ErrorResponseBody
        }
    },
    queryParameters: [Parameters.apiVersion1],
    urlParameters: [Parameters.$host, Parameters.subscriptionId],
    headerParameters: [Parameters.accept],
    serializer
};
const renameOperationSpec = {
    path: "/subscriptions/{subscriptionId}/providers/Microsoft.Subscription/rename",
    httpMethod: "POST",
    responses: {
        200: {
            bodyMapper: Mappers.RenamedSubscriptionId
        },
        default: {
            bodyMapper: Mappers.ErrorResponseBody
        }
    },
    requestBody: Parameters.body,
    queryParameters: [Parameters.apiVersion1],
    urlParameters: [Parameters.$host, Parameters.subscriptionId],
    headerParameters: [Parameters.accept, Parameters.contentType],
    mediaType: "json",
    serializer
};
const enableOperationSpec = {
    path: "/subscriptions/{subscriptionId}/providers/Microsoft.Subscription/enable",
    httpMethod: "POST",
    responses: {
        200: {
            bodyMapper: Mappers.EnabledSubscriptionId
        },
        default: {
            bodyMapper: Mappers.ErrorResponseBody
        }
    },
    queryParameters: [Parameters.apiVersion1],
    urlParameters: [Parameters.$host, Parameters.subscriptionId],
    headerParameters: [Parameters.accept],
    serializer
};
const acceptOwnershipOperationSpec = {
    path: "/providers/Microsoft.Subscription/subscriptions/{subscriptionId}/acceptOwnership",
    httpMethod: "POST",
    responses: {
        200: {
            headersMapper: Mappers.SubscriptionAcceptOwnershipHeaders
        },
        201: {
            headersMapper: Mappers.SubscriptionAcceptOwnershipHeaders
        },
        202: {
            headersMapper: Mappers.SubscriptionAcceptOwnershipHeaders
        },
        204: {
            headersMapper: Mappers.SubscriptionAcceptOwnershipHeaders
        },
        default: {
            bodyMapper: Mappers.ErrorResponseBody
        }
    },
    requestBody: Parameters.body1,
    queryParameters: [Parameters.apiVersion1],
    urlParameters: [Parameters.$host, Parameters.subscriptionId],
    headerParameters: [Parameters.accept, Parameters.contentType],
    mediaType: "json",
    serializer
};
const acceptOwnershipStatusOperationSpec = {
    path: "/providers/Microsoft.Subscription/subscriptions/{subscriptionId}/acceptOwnershipStatus",
    httpMethod: "GET",
    responses: {
        200: {
            bodyMapper: Mappers.AcceptOwnershipStatusResponse
        },
        default: {
            bodyMapper: Mappers.ErrorResponseBody
        }
    },
    queryParameters: [Parameters.apiVersion1],
    urlParameters: [Parameters.$host, Parameters.subscriptionId],
    headerParameters: [Parameters.accept],
    serializer
};
//# sourceMappingURL=subscriptionOperations.js.map